import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosRequest from '../utils/axios';
import generateUUIDs from '../utils/generateUUIDs';

export interface ClaimPrizeState {
    cities: [
        {
            id: number | null;
            name: string | null;
            dcs_county_id: string | null;
        }
    ];
    counties: [
        {
            id: number | null;
            name: string | null;
            short_name: string | null;
        }
    ];
    winnerData: {
        id: number | null;
        token: string | null;
        first_name: string | null;
        last_name: string | null;
        phone_number: string | null;
        email: string | null;
        prize: string | null;
        status: string | null;
        city: string | null;
        county: string | null;
        ops_gift_id: string | null;
        invitation_code: string | null;
        is_redeemed: boolean | null;
        created_at: string | null;
        updated_at: string | null;
        street: string | null;
        street_number: string | null;
        building_number: string | null;
        floor: string | null;
        apartment_number: string | null;
        observations: string | null;
        marketingAgreement: boolean;
        expired_at: string | null;
        image: string | null;
    };

    getCitiesLoading: boolean;
    getCitiesSuccess: boolean;
    getCitiesError: string | null;

    getCountiesLoading: boolean;
    getCountiesSuccess: boolean;
    getCountiesError: string | null;

    getWinnerDataLoading: boolean;
    getWinnerDataSuccess: boolean;
    getWinnerDataError: string | null;

    postWinnerGiftLoading: boolean;
    postWinnerGiftSuccess: boolean;
    postWinnerGiftError: string | null;

    postWinnerInstantGiftLoading: boolean;
    postWinnerInstantGiftSuccess: boolean;
    postWinnerInstantGiftError: string | null;

    postWinnerInvitationLoading: boolean;
    postWinnerInvitationSuccess: boolean;
    postWinnerInvitationError: string | null;
    winnerInvitation: {
        image: string | null;
        pdf: string | null;
        invitation_code?: string | null;
    };
}

const getErrorMessage = (error: string) => {
    switch (error) {
        case 'validation.phone_number':
            return 'Numărul de telefon nu este valid';
        case 'validation.email':
        case 'The email field must be a valid email address.':
            return 'Adresa de email nu este una validă';
        case 'Winner expired.':
            return 'Premiul a expirat';
        default:
            return ' Oops... Ceva nu a mers bine, te rog încearcă din nou!';
    }
};

interface AxiosError {
    response?: {
        statusMessage?: string;
        data?: {
            message?: string;
            invalidField?: {
                fieldErrorDescription?: string;
            };
        };
    };
    message: string;
}

const initialState: ClaimPrizeState = {
    cities: [
        {
            id: null,
            name: null,
            dcs_county_id: null
        }
    ],
    counties: [
        {
            id: null,
            name: null,
            short_name: null
        }
    ],
    winnerData: {
        id: null,
        token: null,
        first_name: null,
        last_name: null,
        phone_number: null,
        email: null,
        prize: null,
        status: null,
        city: null,
        county: null,
        ops_gift_id: null,
        invitation_code: null,
        is_redeemed: null,
        created_at: null,
        updated_at: null,
        street: null,
        street_number: null,
        building_number: null,
        floor: null,
        apartment_number: null,
        observations: null,
        marketingAgreement: false,
        expired_at: null,
        image: null
    },
    getCitiesLoading: false,
    getCitiesSuccess: false,
    getCitiesError: null,

    getCountiesLoading: false,
    getCountiesSuccess: false,
    getCountiesError: null,

    getWinnerDataLoading: false,
    getWinnerDataSuccess: false,
    getWinnerDataError: null,

    postWinnerGiftLoading: false,
    postWinnerGiftSuccess: false,
    postWinnerGiftError: null,

    postWinnerInstantGiftLoading: false,
    postWinnerInstantGiftSuccess: false,
    postWinnerInstantGiftError: null,

    postWinnerInvitationLoading: false,
    postWinnerInvitationSuccess: false,
    postWinnerInvitationError: null,
    winnerInvitation: {
        image: null,
        pdf: null
    }
};

export const getCities = createAsyncThunk(
    'claimPrize/cities',
    async (countyShortName: any, { rejectWithValue }) => {
        try {
            const response = await axiosRequest.get('cities', {
                params: { county_short_name: countyShortName }
            });
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const getCounties = createAsyncThunk(
    'claimPrize/counties',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosRequest.get('counties');
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const getWinnerData = createAsyncThunk(
    'claimPrize/data',
    async (token: string | null, { rejectWithValue }) => {
        try {
            const response = await axiosRequest.get(`winners/${token}`);
            return response.data.winner;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const postWinnerGift = createAsyncThunk(
    'claimPrize/gift',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };

            const response = await axiosRequest.post('winners/gift', payload);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const postWinnerInstantGift = createAsyncThunk(
    'claimPrize/instant-gift',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const payload = ip ? { ...params, ip } : params;
            const response = await axiosRequest.post('/winners/instant-gift', payload);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const postWinnerInvitation = createAsyncThunk(
    'claimPrize/invitation',
    async (params: any, { rejectWithValue }) => {
        try {
            let ip: string | undefined;
            const ipifyRes = await fetch('https://api.ipify.org?format=json');
            if (ipifyRes.ok) {
                const json = (await ipifyRes.json()) as { ip: string };
                ip = json.ip;
            }
            const [storageUUID, cookieUUID, browserUUID] = await generateUUIDs();

            const payload = {
                ...params,
                ...(ip && { ip }),
                storageUUID,
                cookieUUID,
                browserUUID
            };
            const response = await axiosRequest.post('winners/invitation', payload);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const claimPrizeSlice = createSlice({
    name: 'claimPrize',
    initialState,
    reducers: {
        clearPostWinnerGiftError: (state) => {
            state.postWinnerGiftError = null;
            state.postWinnerGiftSuccess = false;
        }
    },
    extraReducers: (builder) => {
        builder

            .addCase(getCities.pending, (state) => {
                state.getCitiesLoading = true;
                state.getCitiesSuccess = false;
                state.getCitiesError = null;
            })
            .addCase(getCities.fulfilled, (state, action) => {
                state.cities = action.payload;
                state.getCitiesLoading = false;
                state.getCitiesSuccess = true;
                state.getCitiesError = null;
            })
            .addCase(getCities.rejected, (state, action) => {
                state.getCitiesLoading = false;
                state.getCitiesSuccess = false;
                state.getCitiesError = action.payload as string;
            })

            .addCase(getCounties.pending, (state) => {
                state.getCountiesLoading = true;
                state.getCountiesSuccess = false;
                state.getCountiesError = null;
            })
            .addCase(getCounties.fulfilled, (state, action) => {
                state.counties = action.payload;
                state.getCountiesLoading = false;
                state.getCountiesSuccess = true;
                state.getCountiesError = null;
            })
            .addCase(getCounties.rejected, (state, action) => {
                state.getCountiesLoading = false;
                state.getCountiesSuccess = false;
                state.getCountiesError = action.payload as string;
            })

            .addCase(getWinnerData.pending, (state) => {
                state.getWinnerDataLoading = true;
                state.getWinnerDataSuccess = false;
                state.getWinnerDataError = null;
            })
            .addCase(getWinnerData.fulfilled, (state, action) => {
                state.winnerData = action.payload;
                state.getWinnerDataLoading = false;
                state.getWinnerDataSuccess = true;
                state.getWinnerDataError = null;
            })
            .addCase(getWinnerData.rejected, (state, action) => {
                state.getWinnerDataLoading = false;
                state.getWinnerDataSuccess = false;
                state.getWinnerDataError = action.payload as string;
            })

            .addCase(postWinnerGift.pending, (state) => {
                state.postWinnerGiftLoading = true;
                state.postWinnerGiftSuccess = false;
                state.postWinnerGiftError = null;
            })
            .addCase(postWinnerGift.fulfilled, (state, action) => {
                state.postWinnerGiftLoading = false;
                state.postWinnerGiftSuccess = true;
                state.postWinnerGiftError = null;
            })
            .addCase(postWinnerGift.rejected, (state, action) => {
                state.postWinnerGiftLoading = false;
                state.postWinnerGiftSuccess = false;
                state.postWinnerGiftError = getErrorMessage(action.payload as string);
            })
            .addCase(postWinnerInstantGift.pending, (state) => {
                state.postWinnerInstantGiftLoading = true;
                state.postWinnerInstantGiftSuccess = false;
                state.postWinnerInstantGiftError = null;
            })
            .addCase(postWinnerInstantGift.fulfilled, (state, action) => {
                state.postWinnerInstantGiftLoading = false;
                state.postWinnerInstantGiftSuccess = true;
                state.postWinnerInstantGiftError = null;
            })
            .addCase(postWinnerInstantGift.rejected, (state, action) => {
                state.postWinnerInstantGiftLoading = false;
                state.postWinnerInstantGiftSuccess = false;
                state.postWinnerInstantGiftError = getErrorMessage(action.payload as string);
            })

            .addCase(postWinnerInvitation.pending, (state) => {
                state.postWinnerInvitationLoading = true;
                state.postWinnerInvitationSuccess = false;
                state.postWinnerInvitationError = null;
            })
            .addCase(postWinnerInvitation.fulfilled, (state, action) => {
                state.winnerInvitation = action.payload;
                state.postWinnerInvitationLoading = false;
                state.postWinnerInvitationSuccess = true;
                state.postWinnerInvitationError = null;
            })
            .addCase(postWinnerInvitation.rejected, (state, action) => {
                state.postWinnerInvitationLoading = false;
                state.postWinnerInvitationSuccess = false;
                state.postWinnerInvitationError = getErrorMessage(action.payload as string);
            });
    }
});

export const { clearPostWinnerGiftError } = claimPrizeSlice.actions;
export default claimPrizeSlice.reducer;
